import {
  CloseButton,
  Drawer,
  Flex,
  Image,
  Input,
  Menu,
  Portal,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import { useState, useEffect, useContext, useMemo } from "react";
import { BsCart2, BsSearch } from "react-icons/bs";
import { FaBars, FaBoxOpen, FaUser, FaUserCircle } from "react-icons/fa";
import { FaRegCircleUser } from "react-icons/fa6";
import { IoExitOutline, IoGridOutline } from "react-icons/io5";
import { PRIMARY_COLOR } from "../../constants/colors";
import { useLocation, useNavigate } from "react-router-dom";
import useCartStore from "../../store/cartStore";
import { AuthContext } from "../../contexts/AuthContext";
import useFilterStore from "../../store/filterStore";
import useCart from "../../hooks/useCart";
import api from "../../services/api";
import { buildLoginPath } from "../../utils/catalogContext";

// Extrai clientHash/sellerId direto da URL quando a rota atual os carrega
// (ex.: /catalogo/:clientHash/:sellerId, /produto/:id/:clientHash/:sellerId).
// Usado no lugar de esperar o localStorage ser preenchido pela página Home,
// que só acontece depois que o Header já pode ter montado e tentado
// hidratar o carrinho.
function getRouteIds(pathname) {
  const parts = pathname.split("/").filter(Boolean);

  if (["catalogo", "carrinho", "checkout"].includes(parts[0])) {
    return { clientHash: parts[1] ?? null, sellerId: parts[2] ?? null };
  }

  if (parts[0] === "produto") {
    return { clientHash: parts[2] ?? null, sellerId: parts[3] ?? null };
  }

  return {
    clientHash: localStorage.getItem("@aqvex/clientHash"),
    sellerId: localStorage.getItem("@aqvex/sellerId"),
  };
}

const DrawerMenu = ({ open, onOpenChange, user, logout, navigate, goToLogin }) => {
  const clientHash = localStorage.getItem("@aqvex/clientHash");
  const sellerId = localStorage.getItem("@aqvex/sellerId");

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} placement="start">
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content padding="15px">
            <Drawer.Header>
              <Drawer.Title>
                <Image src="/logo-aqvex.png" />
              </Drawer.Title>
            </Drawer.Header>

            <Drawer.Body
              padding="20px 0px"
              gap="20px"
              display="flex"
              flexDirection="column"
            >
              {/* --- MENU DE USUÁRIO IGUAL AO DESKTOP --- */}
              <Flex alignItems="center" justifyContent="space-between">
                <Flex gap="10px" alignItems="center">
                  <FaRegCircleUser size={20} />
                  {user?.info?.name ? (
                    <Text fontWeight="bold">Olá, {user.info.name}</Text>
                  ) : (
                    <Text
                      fontWeight="bold"
                      cursor="pointer"
                      onClick={() => {
                        goToLogin();
                        onOpenChange(false);
                      }}
                    >
                      Entre ou cadastre-se
                    </Text>
                  )}
                </Flex>

                {user?.info?.name && (
                  <Flex
                    gap="10px"
                    alignItems="center"
                    cursor="pointer"
                    onClick={logout}
                  >
                    <IoExitOutline size={20} color="red" />
                    <Text color="red">Sair</Text>
                  </Flex>
                )}
              </Flex>

              {/* --- OPÇÕES DO DRAWER --- */}
              {user?.info?.name && (
                <Flex flexDirection="column">
                  {/*   <Flex
                    alignItems="center"
                    gap="10px"
                    padding="15px 0"
                    borderBottom="1px solid #e0e0e0"
                    cursor="pointer"
                    onClick={() => {
                      navigate(`/catalogo/${clientHash}/${sellerId}`);
                      onOpenChange(false);
                    }}
                  >
                    <IoGridOutline size={20} />
                    <Text>Categorias</Text>
                  </Flex>
*/}

                  <Flex
                    alignItems="center"
                    gap="10px"
                    padding="15px 0"
                    borderBottom="1px solid #e0e0e0"
                    cursor="pointer"
                    onClick={() => {
                      if (user?.info?.name) navigate("/minha-conta");
                      else goToLogin();
                      onOpenChange(false);
                    }}
                  >
                    <FaUserCircle size={20} />
                    <Text>Minha Conta</Text>
                  </Flex>

                  {user.info.type === "admin" ? (
                    <Flex
                      alignItems="center"
                      gap="10px"
                      padding="15px 0"
                      borderBottom="1px solid #e0e0e0"
                      cursor="pointer"
                      onClick={() => {
                        if (user?.info?.name) navigate("/painel");
                        else goToLogin();
                        onOpenChange(false);
                      }}
                    >
                      <FaBoxOpen size={20} />
                      <Text>Painel</Text>
                    </Flex>
                  ) : (
                    <Flex
                      alignItems="center"
                      gap="10px"
                      padding="15px 0"
                      borderBottom="1px solid #e0e0e0"
                      cursor="pointer"
                      onClick={() => {
                        if (user?.info?.name) navigate("/meus-pedidos");
                        else goToLogin();
                        onOpenChange(false);
                      }}
                    >
                      <FaBoxOpen size={20} />
                      <Text>Meus Pedidos</Text>
                    </Flex>
                  )}
                </Flex>
              )}
            </Drawer.Body>

            <Drawer.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Drawer.CloseTrigger>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};

export default function Header() {
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const [showSearch, setShowSearch] = useState(false);
  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const [isShrunk, setIsShrunk] = useState(false);
  const [photo, setPhoto] = useState(null);

  const { user, setUser } = useContext(AuthContext);
  const { filterText, setFilterText } = useFilterStore();

  const navigate = useNavigate();
  const location = useLocation();

  const { clientHash, sellerId } = useMemo(
    () => getRouteIds(location.pathname),
    [location.pathname],
  );

  // Busca o carrinho salvo no backend (fonte de verdade, sem localStorage)
  // na montagem — o Header fica montado em toda rota (ver AppRoutes), então
  // isso já garante o badge certo mesmo em quem acabou de abrir o app.
  const { cart } = useCart(clientHash, sellerId);

  const totalProductItems = cart.reduce((sum, item) => {
    const totalQuantity = Object.values(item.sizes).reduce(
      (total, size) => total + (size.quantity || 0),
      0,
    );

    return sum + totalQuantity;
  }, 0);

  useEffect(() => {
    getCompanyPhoto();
  }, []);

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScroll = window.scrollY;

      if (currentScroll > lastScrollY && currentScroll > 80) {
        setIsShrunk(true);
      } else {
        setIsShrunk(false);
      }

      lastScrollY = currentScroll;
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Leva pro login com o catálogo atual explícito na própria URL
  // (/login/:clientHash/:sellerId), pra poder voltar pra ele depois de
  // autenticar em vez de cair em alguma página anterior aleatória.
  const goToLogin = () => {
    navigate(buildLoginPath(clientHash, sellerId), {
      state: clientHash && sellerId ? { from: `/catalogo/${clientHash}/${sellerId}` } : undefined,
    });
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("@aqvex/user");
    localStorage.removeItem("@aqvex/token");
    navigate(`/catalogo/${clientHash}/${sellerId}`);
    // Só limpa a exibição local — o carrinho salvo no backend deve
    // continuar lá para quando o cliente logar de novo.
    useCartStore.getState().clearCart();
  };

  const getCompanyPhoto = async () => {
    await api
      .get(`/admin/profile-photo/${clientHash}`)
      .then((response) => {
        setPhoto(response.data.photo);
      })
      .catch((error) => {
        console.log("erro ao buscar foto", error);
      });
  };

  return (
    <>
      {!window.location.pathname.includes("/painel") && (
        <Flex width="100%" flexDirection="column" position="fixed" zIndex={10}>
          <Flex
            gap="30px"
            width="100%"
            alignItems="center"
            boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
            borderBottom="1px solid #e0e0e0"
            flexDirection="column"
            backgroundColor="white"
            overflow="hidden"
            transition="transform 0.4s ease, padding 0.4s ease"
            transform={isShrunk && !isLargerThan768 && "translateY(-100%)"}
            padding={isShrunk && !isLargerThan768 ? "0px" : "10px 15px"}
          >
            <Flex
              width="100%"
              justifyContent="space-between"
              alignItems="center"
            >
              <Flex alignItems="center" gap="20px">
                {!isLargerThan768 && (
                  <FaBars size={20} onClick={() => setDrawerIsOpen(true)} />
                )}
                <Image
                  src={
                    photo
                      ? `https://api.aqvex.com.br${photo}`
                      : "/logo-aqvex.png"
                  }
                  maxW="50px"
                  cursor="pointer"
                  onClick={() =>
                    user?.info?.type === "admin"
                      ? navigate("/painel")
                      : navigate(`/catalogo/${clientHash}/${sellerId}`)
                  }
                />
              </Flex>

              {/* DESKTOP */}
              {isLargerThan768 && (
                <>
                  {window.location.pathname.includes("catalogo") && (
                    <Input
                      placeholder="Buscar produto (Ex.: PRETO, M ou CONJUNTO, ROSA, GG)"
                      padding="15px"
                      flex={0.8}
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                  )}
                  <Flex gap="10px" alignItems="center" cursor="pointer">
                    <FaUser />

                    {/* --- MENU DO USUÁRIO (DESKTOP) --- */}
                    {user?.info?.name ? (
                      <Menu.Root>
                        <Menu.Trigger>
                          <Text
                            fontSize="14px"
                            cursor="pointer"
                            _hover={{ color: "teal.500" }}
                          >
                            {user.info.name}
                          </Text>
                        </Menu.Trigger>

                        <Menu.Positioner>
                          <Menu.Content
                            borderRadius="md"
                            boxShadow="lg"
                            p="2"
                            bg="white"
                            minW="150px"
                            zIndex={999}
                          >
                            {user.info.type === "admin" ? (
                              <Menu.Item
                                onClick={() => navigate("/painel")}
                                _hover={{ bg: "gray.100" }}
                                cursor="pointer"
                                padding="10px"
                              >
                                Painel
                              </Menu.Item>
                            ) : (
                              <Menu.Item
                                onClick={() => navigate("/meus-pedidos")}
                                _hover={{ bg: "gray.100" }}
                                cursor="pointer"
                                padding="10px"
                              >
                                Meus Pedidos
                              </Menu.Item>
                            )}

                            <Menu.Item
                              onClick={logout}
                              _hover={{ bg: "gray.100" }}
                              cursor="pointer"
                              padding="10px"
                            >
                              Sair
                            </Menu.Item>
                          </Menu.Content>
                        </Menu.Positioner>
                      </Menu.Root>
                    ) : (
                      <Text
                        fontSize="14px"
                        cursor="pointer"
                        onClick={goToLogin}
                      >
                        Entrar
                      </Text>
                    )}

                    {/* CARRINHO DESKTOP */}
                    <Flex position="relative">
                      <BsCart2
                        size={20}
                        onClick={() =>
                          navigate(`/carrinho/${clientHash}/${sellerId}`)
                        }
                        style={{ marginLeft: "20px" }}
                      />
                      <Flex
                        width="15px"
                        height="15px"
                        justifyContent="center"
                        alignItems="center"
                        backgroundColor={PRIMARY_COLOR}
                        color="white"
                        borderRadius="50%"
                        position="absolute"
                        right={-2}
                        bottom={2}
                        fontSize="10px"
                      >
                        {totalProductItems}
                      </Flex>
                    </Flex>
                  </Flex>
                </>
              )}

              {/* MOBILE */}
              {!isLargerThan768 && (
                <Flex alignItems="center" gap="20px">
                  {window.location.pathname.includes("catalogo") && (
                    <BsSearch
                      size={20}
                      onClick={() => setShowSearch((prev) => !prev)}
                    />
                  )}

                  {/* CARRINHO MOBILE */}
                  <Flex position="relative">
                    <BsCart2
                      size={20}
                      onClick={() =>
                        navigate(`/carrinho/${clientHash}/${sellerId}`)
                      }
                    />
                    <Flex
                      width="15px"
                      height="15px"
                      justifyContent="center"
                      alignItems="center"
                      backgroundColor={PRIMARY_COLOR}
                      color="white"
                      borderRadius="50%"
                      position="absolute"
                      right={-2}
                      bottom={2}
                      fontSize="10px"
                    >
                      {totalProductItems}
                    </Flex>
                  </Flex>
                </Flex>
              )}
            </Flex>

            {!isLargerThan768 && showSearch && (
              <Input
                placeholder="Buscar produto (Ex.: PRETO, M ou CONJUNTO, ROSA, GG)"
                padding="15px"
                w="100%"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            )}
          </Flex>

          {/* --- DRAWER COM LÓGICA REAL DE USUÁRIO --- */}
          <DrawerMenu
            open={drawerIsOpen}
            onOpenChange={() => setDrawerIsOpen((prev) => !prev)}
            user={user}
            logout={logout}
            navigate={navigate}
            goToLogin={goToLogin}
          />
        </Flex>
      )}
    </>
  );
}
